import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { DealMarker, PortfolioMarker, CompetitionMarker } from './markers';
import { useDealLocations } from './mapHooks';
import { ColorPaletteGenerator } from 'utils/colorUtils';
import { batchGeocodeLocations } from 'utils/geocode-util';
import { IoExpand, IoContract } from 'react-icons/io5';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from "@chakra-ui/modal";

// Helper function to convert to sentence case (first letter capitalized)
const toSentenceCase = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Error boundary for map-related errors
class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error but don't crash the app
    console.warn('Map error caught by boundary:', error.message);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full w-full flex items-center justify-center bg-gray-100 rounded-lg">
          <p className="text-lg text-gray-500">Map temporarily unavailable</p>
        </div>
      );
    }

    return this.props.children;
  }
}

const BoundsUpdater = React.memo(({ locations, selectedDeals, allLocations }) => {
  const map = useMap();

  useEffect(() => {
    // Add safety check to prevent errors during view mode transitions
    if (!map || !map.getContainer()) {
      return;
    }

    try {
      // Determine which locations to focus on
      let locationsToFocus = [];

      // If we have selected deals, focus only on those deal locations
      if (selectedDeals && selectedDeals.size > 0 && allLocations?.length > 0) {
        locationsToFocus = allLocations.filter(loc =>
          selectedDeals.has(loc.Deal) && loc.lat && loc.lng
        );
      }

      // If no deals selected or no deal locations found, use all provided locations
      if (locationsToFocus.length === 0 && locations?.length > 0) {
        locationsToFocus = locations;
      }

      if (locationsToFocus?.length > 0) {
        const validLocations = locationsToFocus.filter(loc => {
          // Check for array coordinates
          if (Array.isArray(loc.coordinates) && loc.coordinates.length === 2) {
            return true;
          }

          // Check for object coordinates with latitude/longitude
          if (loc.coordinates && typeof loc.coordinates === 'object' &&
            loc.coordinates.latitude !== undefined && loc.coordinates.longitude !== undefined) {
            return true;
          }

          // Check for lat/lng properties
          if (loc.lat !== undefined && loc.lng !== undefined) {
            return true;
          }

          return false;
        });

        if (validLocations.length > 0) {
          const bounds = L.latLngBounds(
            validLocations.map(loc => {
              // Handle array coordinates
              if (Array.isArray(loc.coordinates) && loc.coordinates.length === 2) {
                return loc.coordinates;
              }

              // Handle object coordinates with latitude/longitude
              if (loc.coordinates && typeof loc.coordinates === 'object' &&
                loc.coordinates.latitude !== undefined && loc.coordinates.longitude !== undefined) {
                return [parseFloat(loc.coordinates.latitude), parseFloat(loc.coordinates.longitude)];
              }

              // Handle lat/lng properties
              return [loc.lat, loc.lng];
            })
          );

          // Use tighter bounds when focusing on selected deals
          const isSelectedDealsView = selectedDeals && selectedDeals.size > 0 &&
            locationsToFocus.some(loc => selectedDeals.has(loc.Deal));

          // Get responsive padding based on screen size
          const getResponsivePadding = () => {
            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;

            // Calculate padding as percentage of screen size, with minimums
            const widthPadding = Math.max(20, Math.min(50, screenWidth * 0.05));
            const heightPadding = Math.max(20, Math.min(50, screenHeight * 0.05));

            if (isSelectedDealsView) {
              // Tighter padding for selected deals, but still responsive
              return [Math.max(15, widthPadding * 0.6), Math.max(15, heightPadding * 0.6)];
            } else {
              return [widthPadding, heightPadding];
            }
          };

          const options = {
            padding: getResponsivePadding(),
            maxZoom: isSelectedDealsView ? 5 : 7
          };

          map.fitBounds(bounds, options);
        }
      }
    } catch (error) {
      // Silently handle Leaflet positioning errors during view mode transitions
      console.warn('Map bounds update error (safely ignored):', error.message);
    }
  }, [locations, selectedDeals, allLocations, map]);

  return null;
});

const MarkerLayer = React.memo(({
  locations,
  dealColors,
  currentDealName,
  selectedDeals,
  portfolioLocations = [],
  isPortfolioMode = false
}) => {
  return locations.map((facility) => {
    const isCurrentDeal = facility.Deal === currentDealName;
    const isHighlighted = selectedDeals.has(facility.Deal);
    const zIndex = isCurrentDeal ? 2000 : selectedDeals.has(facility.Deal) ? 1000 : 0;

    return (
      <DealMarker
        key={`${facility.Facilities}-${facility.Deal}`}
        facility={facility}
        color={dealColors[facility.Deal]}
        isHighlighted={isHighlighted}
        isCurrentDeal={isCurrentDeal}
        zIndex={zIndex}
        portfolioFacilities={portfolioLocations}
        isPortfolioMode={isPortfolioMode}
        isNonGroupFacility={!!facility._isNonGroupFacility}
      />
    );
  });
});


const PortfolioLayer = React.memo(({
  locations,
  colors
}) => {
  return locations.map((location, index) => (
    <PortfolioMarker
      key={`${location.facilityName}-${index}`}
      location={location}
      color={colors[location.details.operator]}
    />
  ));
});

const CompetitionLayer = React.memo(({
  locations,
  color = "#FF6B6B"
}) => {
  if (!locations || locations.length === 0) {
    return null;
  }

  return locations.map((facility, index) => (
    <CompetitionMarker
      key={`competition-${facility.name || facility.facilityName || index}`}
      facility={facility}
      color={color}
    />
  ));
});

const MapLegend = React.memo(({
  dealColors,
  selectedDeals,
  onToggleDeal,
  showPortfolio,
  onTogglePortfolio,
  showCompetition,
  onToggleCompetition,
  isPortfolioMode = false
}) => (
  <div className="absolute top-2 right-2 z-[1000] bg-white/90 p-1 sm:p-2 rounded-lg shadow-md
    max-h-[150px] sm:max-h-[calc(100%-1rem)] 
    w-[150px] sm:w-[200px]
    overflow-y-auto"
  >
    <div className="mb-0.5 border-b pb-0.5 space-y-1">
      {!isPortfolioMode && (
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={showPortfolio}
            onChange={onTogglePortfolio}
            className="form-checkbox h-2.5 w-2.5 text-blue-600"
          />
          <span className="text-[10px] sm:text-xs font-semibold">Portfolio Facilities</span>
        </label>
      )}
      <label className="flex items-center gap-1">
        <input
          type="checkbox"
          checked={showCompetition}
          onChange={onToggleCompetition}
          className="form-checkbox h-2.5 w-2.5 text-red-600"
        />
        <span className="text-[10px] sm:text-xs font-semibold">Competition Facilities</span>
      </label>
    </div>

    <div>
      <p className="font-bold mb-0.5 text-[10px] sm:text-xs">Deals</p>
      <div className="max-h-[80px] sm:max-h-[300px] overflow-y-auto">
        {Object.entries(dealColors).map(([dealName, color]) => {
          // Display portfolio names in sentence case, but keep lowercase key for data access
          const displayName = isPortfolioMode ? toSentenceCase(dealName) : dealName;
          return (
            <div
              key={dealName}
              className={`flex items-center gap-1 py-0.5 ml-1 rounded cursor-pointer
                ${selectedDeals.has(dealName) ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              onClick={() => onToggleDeal(dealName)}
            >
              <div
                className={`w-2 h-2 rounded-full transition-transform duration-200
                  ${selectedDeals.has(dealName) ? 'scale-125 ring-1 ring-gray-400' : ''}`}
                style={{ backgroundColor: color }}
              />
              <span className={`text-[10px] sm:text-xs ${selectedDeals.has(dealName) ? 'font-semibold' : ''}`}>
                {displayName}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </div>
));

// Simplified MapContent for normal view
const NormalMapContent = React.memo(({
  dealColors,
  selectedDeals,
  filteredLocations,
  visibleLocations,
  portfolioLocations,
  competitionLocations,
  operatorColors,
  currentDealName,
  showPortfolio,
  showCompetition,
  onToggleDeal,
  onTogglePortfolio,
  onToggleCompetition,
  isPortfolioMode = false
}) => (
  <>
    <MapLegend
      dealColors={dealColors}
      selectedDeals={selectedDeals}
      onToggleDeal={onToggleDeal}
      showPortfolio={showPortfolio}
      onTogglePortfolio={onTogglePortfolio}
      showCompetition={showCompetition}
      onToggleCompetition={onToggleCompetition}
      isPortfolioMode={isPortfolioMode}
    />

    <MapContainer
      key={`map-${isPortfolioMode ? 'portfolio' : 'deals'}`}
      center={[39.8283, -98.5795]}
      zoom={4}
      className="h-full z-50"
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      />

      <BoundsUpdater
        locations={[...filteredLocations, ...portfolioLocations, ...competitionLocations]}
        selectedDeals={selectedDeals}
        allLocations={visibleLocations}
      />

      <MarkerLayer
        locations={filteredLocations}
        dealColors={dealColors}
        currentDealName={currentDealName}
        selectedDeals={selectedDeals}
        portfolioLocations={portfolioLocations}
        isPortfolioMode={isPortfolioMode}
      />

      {showPortfolio && (
        <PortfolioLayer
          locations={portfolioLocations}
          colors={operatorColors}
        />
      )}

      {showCompetition && (
        <CompetitionLayer
          locations={competitionLocations || []}
        />
      )}
    </MapContainer>
  </>
));

// FullscreenMapContent with portfolio locations passed to MarkerLayer
const FullscreenMapContent = React.memo(({
  dealColors,
  selectedDeals,
  filteredLocations,
  visibleLocations,
  portfolioLocations,
  competitionLocations,
  operatorColors,
  currentDealName,
  showPortfolio,
  showCompetition,
  onToggleDeal,
  onTogglePortfolio,
  onToggleCompetition,
  isPortfolioMode = false
}) => {
  const mapRef = React.useRef(null);

  // Use a longer timeout and track mounted state
  useEffect(() => {
    let timeoutId;
    // Use a much longer timeout to ensure DOM is fully rendered
    timeoutId = setTimeout(() => {
      if (mapRef.current) {
        const leafletInstance = mapRef.current._leafletContainer;
        if (leafletInstance && leafletInstance.invalidateSize) {
          leafletInstance.invalidateSize(true);
        }
      }
    }, 500); // Increased timeout

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      <MapLegend
        dealColors={dealColors}
        selectedDeals={selectedDeals}
        onToggleDeal={onToggleDeal}
        showPortfolio={showPortfolio}
        onTogglePortfolio={onTogglePortfolio}
        showCompetition={showCompetition}
        onToggleCompetition={onToggleCompetition}
        isPortfolioMode={isPortfolioMode}
      />

      <div className="h-full w-full" ref={mapRef}>
        <MapContainer
          key={`fullscreen-map-${isPortfolioMode ? 'portfolio' : 'deals'}`}
          center={[39.8283, -98.5795]}
          zoom={4}
          className="h-full w-full z-50"
          whenReady={(map) => {
            mapRef.current = { _leafletContainer: map.target };
          }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />

          <BoundsUpdater
            locations={[...filteredLocations, ...portfolioLocations, ...competitionLocations]}
            selectedDeals={selectedDeals}
            allLocations={visibleLocations}
          />

          <MarkerLayer
            locations={filteredLocations}
            dealColors={dealColors}
            currentDealName={currentDealName}
            selectedDeals={selectedDeals}
            portfolioLocations={portfolioLocations}
            isPortfolioMode={isPortfolioMode}
          />

          {showPortfolio && (
            <PortfolioLayer
              locations={portfolioLocations}
              colors={operatorColors}
            />
          )}

          {showCompetition && (
            <CompetitionLayer
              locations={competitionLocations || []}
            />
          )}
        </MapContainer>
      </div>
    </>
  );
});

const DealsMap = React.memo(({
  consolidatedData,
  currentDealName,
  portfolioFacilities,
  competitionData,
  portfolioCompetitionData,
  currentGroup,
  isPortfolioMode = false
}) => {
  const [showPortfolio, setShowPortfolio] = useState(!isPortfolioMode);
  const [showCompetition, setShowCompetition] = useState(false);
  const [error, setError] = useState(null);
  const [dealColors, setDealColors] = useState({});
  const [operatorColors, setOperatorColors] = useState({});
  const [portfolioLocations, setPortfolioLocations] = useState([]);
  const [competitionLocations, setCompetitionLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Cleanup effect for view mode changes
  useEffect(() => {
    return () => {
      // Clear any remaining timeouts when component unmounts
      // This helps prevent Leaflet errors during view mode transitions
    };
  }, []);

  const {
    selectedDeals,
    visibleLocations,
    toggleDeal,
    progress
  } = useDealLocations(consolidatedData, currentDealName, currentGroup);

  const handleTogglePortfolio = useCallback((e) => {
    setShowPortfolio(e.target.checked);
  }, []);

  const handleToggleCompetition = useCallback((e) => {
    setShowCompetition(e.target.checked);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Initialize colors and portfolio locations
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);

        // Generate colors for deals
        if (consolidatedData) {
          const dealNames = Object.keys(consolidatedData);
          const colors = ColorPaletteGenerator.generateConsistentColorMap(dealNames, {
            mode: 'standard'
          });
          setDealColors(colors);
        }

        // Generate colors and process portfolio locations
        if (portfolioFacilities?.portfolioProperties) {
          const properties = portfolioFacilities.portfolioProperties;
          const operators = [...new Set(properties.map(f => f.details?.operator))];

          const colors = ColorPaletteGenerator.generateConsistentColorMap(operators, {
            mode: 'theme'
          });
          setOperatorColors(colors);

          // First check for existing coordinates, then geocode only what's needed
          const locationsWithCoordinates = [];
          const locationsNeedingGeocode = [];

          properties.forEach(facility => {
            // Check if facility already has coordinates (try both uppercase and lowercase)
            const coordinatesField = facility.details?.Coordinates || facility.details?.coordinates || facility.coordinates;

            if (coordinatesField) {
              try {
                // Parse the coordinates field - it might be a string or already parsed
                let coordinates;
                if (typeof coordinatesField === 'string') {
                  coordinates = JSON.parse(coordinatesField);
                } else {
                  coordinates = coordinatesField;
                }

                // Handle different coordinate formats
                if (Array.isArray(coordinates) && coordinates.length === 2) {
                  // Already in [lat, lng] format
                  locationsWithCoordinates.push({
                    ...facility,
                    coordinates: coordinates
                  });
                } else if (coordinates && typeof coordinates === 'object' &&
                  (coordinates.latitude !== undefined && coordinates.longitude !== undefined)) {
                  // Object format with lowercase lat/lng properties
                  locationsWithCoordinates.push({
                    ...facility,
                    coordinates: [parseFloat(coordinates.latitude), parseFloat(coordinates.longitude)]
                  });
                } else if (coordinates && typeof coordinates === 'object' &&
                  (coordinates.Latitude !== undefined && coordinates.Longitude !== undefined)) {
                  // Object format with uppercase lat/lng properties
                  locationsWithCoordinates.push({
                    ...facility,
                    coordinates: [parseFloat(coordinates.Latitude), parseFloat(coordinates.Longitude)]
                  });
                } else {
                  // Invalid coordinates format, need to geocode
                  locationsNeedingGeocode.push(facility);
                }
              } catch (error) {
                console.warn('Error parsing coordinates for facility:', facility.facilityName, error);
                locationsNeedingGeocode.push(facility);
              }
            } else {
              // No coordinates field, need to geocode
              locationsNeedingGeocode.push(facility);
            }
          });

          // Geocode locations that don't have coordinates
          let geocodedPortfolio = [...locationsWithCoordinates];

          if (locationsNeedingGeocode.length > 0) {
            const geocoded = await batchGeocodeLocations(
              locationsNeedingGeocode.map(facility => ({
                ...facility,
                // Fix field mapping - portfolio data uses "City/Town" not "city"
                city: facility["City/Town"] || facility.city,
                state: facility.state,
                operator: facility.details?.operator
              }))
            );
            geocodedPortfolio = [...geocodedPortfolio, ...geocoded];
          }

          setPortfolioLocations(geocodedPortfolio);
        }
      } catch (err) {
        setError('Error initializing map data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [consolidatedData, portfolioFacilities]);

  // Process competition data when deals change
  useEffect(() => {
    // Skip processing if competition display is off
    if (!showCompetition) {
      return;
    }

    const processCompetitionData = async () => {
      // Use appropriate competition data based on mode
      const activeCompetitionData = isPortfolioMode ? portfolioCompetitionData : competitionData;

      // First check if we have competition data
      if (!activeCompetitionData) {
        setCompetitionLocations([]);
        return;
      }

      try {
        // Helper function to convert to sentence case
        const toSentenceCase = (str) => {
          if (!str) return str;
          return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        // Helper function to find the matching key in competition data
        const findCompetitionKey = (dealName, availableKeys) => {
          if (!dealName) return null;

          // Try multiple key formats:
          // 1. "Portfolio - [SentenceCase]" (e.g., "Portfolio - Crown")
          const sentenceCaseName = toSentenceCase(dealName);
          const portfolioKeySentenceCase = `Portfolio - ${sentenceCaseName}`;

          // 2. "Portfolio - [lowercase]" (e.g., "Portfolio - crown")
          const portfolioKeyLowercase = `Portfolio - ${dealName}`;

          // Find which key actually exists in the data
          if (availableKeys.includes(portfolioKeySentenceCase)) {
            return portfolioKeySentenceCase;
          } else if (availableKeys.includes(portfolioKeyLowercase)) {
            return portfolioKeyLowercase;
          } else if (availableKeys.includes(sentenceCaseName)) {
            return sentenceCaseName;
          } else if (availableKeys.includes(dealName)) {
            return dealName;
          } else {
            // Try case-insensitive match
            const caseInsensitiveMatch = availableKeys.find(
              key => key.toLowerCase() === portfolioKeySentenceCase.toLowerCase() ||
                key.toLowerCase() === portfolioKeyLowercase.toLowerCase() ||
                key.toLowerCase() === sentenceCaseName.toLowerCase() ||
                key.toLowerCase() === dealName.toLowerCase()
            );
            return caseInsensitiveMatch || null;
          }
        };

        // Get all available keys in the competition data
        const availableKeys = Object.keys(activeCompetitionData);
        const dealsToProcess = new Set();

        // For portfolio mode, convert all selected deals and current deal name to competition data keys
        if (isPortfolioMode) {
          // Convert selectedDeals to competition data keys
          selectedDeals.forEach(dealName => {
            const competitionKey = findCompetitionKey(dealName, availableKeys);
            if (competitionKey) {
              dealsToProcess.add(competitionKey);
            }
          });

          // Also add currentDealName if it exists
          if (currentDealName) {
            const competitionKey = findCompetitionKey(currentDealName, availableKeys);
            if (competitionKey) {
              dealsToProcess.add(competitionKey);
            }
          }
        } else {
          // For non-portfolio mode, use selectedDeals and currentDealName as-is
          selectedDeals.forEach(dealName => {
            if (activeCompetitionData[dealName]) {
              dealsToProcess.add(dealName);
            }
          });

          if (currentDealName && activeCompetitionData[currentDealName]) {
            dealsToProcess.add(currentDealName);
          }
        }

        // If no deals are selected or current, we can't show competition
        if (dealsToProcess.size === 0) {
          setCompetitionLocations([]);
          return;
        }

        // Filter competition data for selected deals
        let competitionFacilities = [];

        // Collect all competition facilities for the selected deals
        dealsToProcess.forEach(dealName => {
          if (activeCompetitionData[dealName]) {
            const dealCompetition = activeCompetitionData[dealName];

            // This is a nested object structure with facility IDs as keys
            // Extract both the main facility and its competition facilities
            Object.entries(dealCompetition).forEach(([facilityId, facilityData]) => {
              // When a group is selected, only show competition for that group's facilities
              if (currentGroup?.facilityIds?.length > 0 &&
                  dealName === (currentGroup.dealId || currentGroup.portfolioId)) {
                if (!currentGroup.facilityIds.includes(facilityId)) return;
              }

              // Extract the main facility data (which contains coordinates)
              if (facilityData.data) {
                const mainFacility = {
                  ...facilityData.data,
                  id: facilityId,
                  type: 'main',
                  dealName
                };
                competitionFacilities.push(mainFacility);
              }

              // Extract the "closest facilities" which are competition
              if (facilityData['closest facilities'] && Array.isArray(facilityData['closest facilities'])) {

                // Each competition facility has the actual data nested in "facility data"
                facilityData['closest facilities'].forEach((compFacility, index) => {
                  if (compFacility['facility data']) {
                    const processedFacility = {
                      ...compFacility['facility data'],
                      id: compFacility.ccn || `comp-${facilityId}-${index}`,
                      type: 'competition',
                      dealName,
                      referenceFacilityId: facilityId
                    };
                    competitionFacilities.push(processedFacility);
                  }
                });
              }
            });
          }
        });

        if (competitionFacilities.length === 0) {
          setCompetitionLocations([]);
          return;
        }

        // Normalize coordinate properties - handle different property naming conventions
        const normalizedFacilities = competitionFacilities.map(facility => {
          // Make sure we're working with an object
          if (!facility || typeof facility !== 'object') {
            return null;
          }

          const normalized = { ...facility };

          // Handle different coordinate property names including capitalized ones
          if (!normalized.lat && !normalized.lng) {
            // Try capitalized Latitude/Longitude (from the logs, this is the actual format)
            if (normalized.Latitude !== undefined && normalized.Longitude !== undefined) {
              normalized.lat = parseFloat(normalized.Latitude);
              normalized.lng = parseFloat(normalized.Longitude);
            }
            // Try lowercase latitude/longitude
            else if (normalized.latitude !== undefined && normalized.longitude !== undefined) {
              normalized.lat = parseFloat(normalized.latitude);
              normalized.lng = parseFloat(normalized.longitude);
            }
            // Try lat/long
            else if (normalized.lat !== undefined && normalized.long !== undefined) {
              normalized.lng = parseFloat(normalized.long);
            }
          }

          // Make sure name is set for display
          if (!normalized.name && normalized.Facility) {
            normalized.name = normalized.Facility;
          }

          return normalized;
        }).filter(Boolean); // Remove any null items

        // Remove duplicates based on id or name
        const uniqueFacilities = Array.from(
          new Map(normalizedFacilities.map(item => {
            const key = item.id || item.name ||
              (item.Facility ? item.Facility : '') ||
              (item.lat && item.lng ? `${item.lat}-${item.lng}` : `item-${Math.random()}`);
            return [key, item];
          })).values()
        );

        // Check if the facilities already have coordinates
        const facilitiesToGeocode = uniqueFacilities.filter(f =>
          !(f.coordinates?.length === 2 || (f.lat && f.lng))
        );

        let geocodedFacilities = uniqueFacilities.filter(f =>
          f.coordinates?.length === 2 || (f.lat && f.lng)
        );

        // Only geocode facilities that don't already have coordinates
        if (facilitiesToGeocode.length > 0) {
          try {
            const newlyGeocoded = await batchGeocodeLocations(
              facilitiesToGeocode.map(facility => ({
                ...facility,
                city: facility.city || '',
                state: facility.State || facility.state || ''
              }))
            );
            geocodedFacilities = [...geocodedFacilities, ...newlyGeocoded];
          } catch (geocodeError) {
            // Silent fail on geocoding error
          }
        }

        // Filter out any that still don't have coordinates
        const validFacilities = geocodedFacilities.filter(f => {
          const hasCoordinates = f.coordinates?.length === 2 || (f.lat && f.lng);
          return hasCoordinates;
        });

        // Force an update even if the array size hasn't changed
        setCompetitionLocations([...validFacilities]);
      } catch (err) {
        setCompetitionLocations([]);
      }
    };

    processCompetitionData();
  }, [competitionData, portfolioCompetitionData, selectedDeals, currentDealName, showCompetition, isPortfolioMode]);

  // Memoize filtered locations
  const filteredLocations = useMemo(() =>
    visibleLocations.filter(loc => loc.lat && loc.lng),
    [visibleLocations]
  );

  if (loading && progress < 100) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center bg-gray-100 rounded-lg">
        <p className="text-lg mb-4">Loading facilities map...</p>
        <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-sm text-gray-600 mt-2">{progress}% complete</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-100 rounded-lg">
        <p className="text-lg text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden shadow-lg">
      {/* Expand button */}
      <button
        onClick={toggleFullscreen}
        className="absolute top-2 left-2 z-[1000] bg-white rounded-lg p-2 shadow-md hover:bg-gray-100 transition-colors"
        aria-label="Toggle fullscreen map"
      >
        <IoExpand className="text-gray-700" size={20} />
      </button>

      <MapErrorBoundary>
        <NormalMapContent
          dealColors={dealColors}
          selectedDeals={selectedDeals}
          filteredLocations={filteredLocations}
          visibleLocations={visibleLocations}
          portfolioLocations={portfolioLocations}
          competitionLocations={competitionLocations}
          operatorColors={operatorColors}
          currentDealName={currentDealName}
          showPortfolio={showPortfolio}
          showCompetition={showCompetition}
          onToggleDeal={toggleDeal}
          onTogglePortfolio={handleTogglePortfolio}
          onToggleCompetition={handleToggleCompetition}
          isPortfolioMode={isPortfolioMode}
        />
      </MapErrorBoundary>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <Modal
          isOpen={isFullscreen}
          onClose={toggleFullscreen}
          size="full"
          motionPreset="slideInBottom"
        >
          <ModalOverlay className="bg-[#405F78] !opacity-20 z-30" />
          <ModalContent className="!h-[calc(100vh-80px)] !p-6 !top-[80px]">
            {/* <ModalCloseButton className="absolute right-[54px] top-[54px] z-[2000] bg-white rounded-full p-1" /> */}
            <ModalBody className="p-0 h-full">
              <div className="relative w-full h-full">
                {/* Contract button in fullscreen mode */}
                <button
                  onClick={toggleFullscreen}
                  className="absolute top-2 left-2 z-[2000] bg-white rounded-lg p-2 shadow-md hover:bg-gray-100 transition-colors"
                  aria-label="Exit fullscreen"
                >
                  <IoContract className="text-gray-700" size={20} />
                </button>

                <MapErrorBoundary>
                  <FullscreenMapContent
                    dealColors={dealColors}
                    selectedDeals={selectedDeals}
                    filteredLocations={filteredLocations}
                    visibleLocations={visibleLocations}
                    portfolioLocations={portfolioLocations}
                    competitionLocations={competitionLocations}
                    operatorColors={operatorColors}
                    currentDealName={currentDealName}
                    showPortfolio={showPortfolio}
                    showCompetition={showCompetition}
                    onToggleDeal={toggleDeal}
                    onTogglePortfolio={handleTogglePortfolio}
                    onToggleCompetition={handleToggleCompetition}
                    isPortfolioMode={isPortfolioMode}
                  />
                </MapErrorBoundary>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
});

export default DealsMap;